// ===== 3. Cập nhật NotificationSystem Component =====
// File: src/components/NotificationSystem.js

import React, { useState, useEffect } from 'react';
import { Bell, X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const NotificationSystem = () => {
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        // Listen for custom notification events from App component
        const handleShowNotification = (event) => {
            const notification = event.detail;
            addNotification(notification);
        };

        window.addEventListener('showNotification', handleShowNotification);

        return () => {
            window.removeEventListener('showNotification', handleShowNotification);
        };
    }, []);

    const addNotification = (notification) => {
        const notificationWithId = {
            id: notification.id || Date.now(),
            title: notification.title,
            message: notification.message,
            type: notification.type || 'info',
            onClick: notification.onClick,
            duration: notification.duration || 5000,
            timestamp: new Date()
        };

        setNotifications(prev => [...prev, notificationWithId]);

        // Auto remove after duration
        if (notificationWithId.duration > 0) {
            setTimeout(() => {
                removeNotification(notificationWithId.id);
            }, notificationWithId.duration);
        }
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const handleNotificationClick = (notification) => {
        if (notification.onClick) {
            notification.onClick();
        }
        removeNotification(notification.id);
    };

    const getIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
            case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
            default: return <Info className="w-5 h-5 text-blue-500" />;
        }
    };

    const getColorClasses = (type) => {
        switch (type) {
            case 'success': return 'bg-green-50 border-green-200 text-green-800';
            case 'error': return 'bg-red-50 border-red-200 text-red-800';
            case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
            default: return 'bg-blue-50 border-blue-200 text-blue-800';
        }
    };

    const getAnimationClass = (index) => {
        return `animate-in slide-in-from-right duration-300`;
    };

    if (notifications.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
            {notifications.map((notification, index) => (
                <div
                    key={notification.id}
                    className={`p-4 rounded-lg border shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl ${getColorClasses(notification.type)} ${getAnimationClass(index)}`}
                    onClick={() => handleNotificationClick(notification)}
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                            {getIcon(notification.type)}
                            <div className="flex-1 min-w-0">
                                {notification.title && (
                                    <h4 className="font-medium text-sm mb-1">{notification.title}</h4>
                                )}
                                <p className="text-sm leading-relaxed">{notification.message}</p>
                                
                                {/* Action hint */}
                                {notification.onClick && (
                                    <p className="text-xs opacity-75 mt-2">👆 Click để xem chi tiết</p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                removeNotification(notification.id);
                            }}
                            className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default NotificationSystem;